"use client";

import { c as _c } from "react/compiler-runtime";
import { Session } from "@supabase/supabase-js";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { getUserFromDB } from "@/lib/user";
import { createClient } from "@/lib/supabase/client";
type UserContextValue = {
  session: Session | null;
  user: {
    "id": string;
    "email": string;
    "full_name": string | null;
    "avatar_url": string | null;
    "created_at": string | null;
    "updated_at": string | null;
    "active_subscription": {
      "id": number;
      "plan_code": string;
      "status": "active" | "trialing";
      "current_period_start": string;
      "current_period_end": string;
      "cancel_at_period_end": boolean;
    } | null;
  } | null;
};

// The profile shape the root layout loads server-side and hands down. Exported
// so `loadRootSession` builds exactly this and nothing forks the definition.
export type AppUser = UserContextValue["user"];
const UserContext = createContext<UserContextValue | undefined>(undefined);
export function UserProvider(t0) {
  const $ = _c(28);
  const {
    session,
    user,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const supabase = t1;
  const [currentSession, setCurrentSession] = useState(session);
  const refreshTimeoutRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = nextSession => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (!nextSession?.expires_at) {
        return;
      }
      const expiresAtMs = nextSession.expires_at * 1000;
      const delayMs = Math.max(expiresAtMs - Date.now() - 60000, 0);
      refreshTimeoutRef.current = setTimeout(async () => {
        const {
          data,
          error
        } = await supabase.auth.refreshSession();
        if (!error) {
          setCurrentSession(data.session);
        }
      }, delayMs);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scheduleRefresh = t2;
  let t3;
  let t4;
  if ($[2] !== session) {
    t3 = () => {
      setCurrentSession(session);
    };
    t4 = [session];
    $[2] = session;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      let active = true;
      supabase.auth.getSession().then(t7 => {
        const {
          data: data_0
        } = t7;
        if (active && data_0.session) {
          setCurrentSession(data_0.session);
        }
      });
      const {
        data: data_1
      } = supabase.auth.onAuthStateChange((event, nextSession_0) => {
        setCurrentSession(nextSession_0);
        if (event === "SIGNED_OUT") {
          posthog.reset();
        }
      });
      return () => {
        active = false;
        data_1.subscription.unsubscribe();
      };
    };
    t6 = [supabase];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== currentSession) {
    t7 = () => {
      scheduleRefresh(currentSession);
      return () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
      };
    };
    t8 = [currentSession, scheduleRefresh];
    $[7] = currentSession;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = ["user"];
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = !!currentSession?.access_token;
  let t11;
  if ($[11] !== t10 || $[12] !== user) {
    t11 = {
      queryKey: t9,
      queryFn: getUserFromDB,
      initialData: user,
      enabled: t10,
      refetchInterval: 30000,
      refetchIntervalInBackground: true
    };
    $[11] = t10;
    $[12] = user;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const {
    data: freshUser
  } = useQuery(t11);
  const effectiveUser = freshUser ?? user;
  let t12;
  if ($[14] !== effectiveUser) {
    t12 = () => {
      if (!effectiveUser?.id) {
        return;
      }
      posthog.identify(effectiveUser.id, {
        email: effectiveUser.email,
        plan_code: effectiveUser.active_subscription?.plan_code ?? "free",
        subscription_status: effectiveUser.active_subscription?.status ?? "none",
        created_at: effectiveUser.created_at
      });
    };
    $[14] = effectiveUser;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = effectiveUser?.id;
  const t14 = effectiveUser?.email;
  const t15 = effectiveUser?.active_subscription?.plan_code;
  const t16 = effectiveUser?.active_subscription?.status;
  const t17 = effectiveUser?.created_at;
  let t18;
  if ($[16] !== t13 || $[17] !== t14 || $[18] !== t15 || $[19] !== t16 || $[20] !== t17) {
    t18 = [t13, t14, t15, t16, t17];
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  useEffect(t12, t18);
  let t19;
  if ($[22] !== currentSession || $[23] !== effectiveUser) {
    t19 = {
      session: currentSession,
      user: effectiveUser
    };
    $[22] = currentSession;
    $[23] = effectiveUser;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== children || $[26] !== t19) {
    t20 = <UserContext.Provider value={t19}>{children}</UserContext.Provider>;
    $[25] = children;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  return t20;
}
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
}